import { Card } from "@/components/ui/card";
import {
    Webhook,
    Phone,
    PhoneOff,
    PhoneForwarded,
    BookOpen,
    Cpu,
    Voicemail,
    GitBranch,
    Calendar,
    Clock,
    Blocks,
    MessageSquare,
    Zap,
    Hash,
    FileText,
    Globe,
    ArrowRightLeft,
    ListTree,
    KeyRound
} from "lucide-react";
import { Tool } from "@/api/tools/tools.types";
import { motion } from "framer-motion";
import { FadeId } from "@/components/ui/fade-id";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

interface ToolCardProps {
    tool: Tool;
    onClick: () => void;
    variants?: any;
}

const TYPE_ICONS = {
    function: Webhook,
    dtmf: Hash,
    endCall: PhoneOff,
    transferCall: PhoneForwarded,
    output: MessageSquare,
    mcp: Cpu,
    query: BookOpen,
    voicemail: Voicemail,
    handoff: GitBranch
} as const;

const TYPE_LABELS = {
    function: "Function",
    dtmf: "DTMF",
    endCall: "End Call",
    transferCall: "Transfer",
    output: "Output",
    mcp: "MCP",
    query: "QKnowledge",
    voicemail: "Voicemail",
    handoff: "TransAssist"
} as const;

// StatButton Component
const StatButton = ({ 
    icon: Icon, 
    value, 
    label, 
    tooltip,
    active = false
}: { 
    icon: any; 
    value: string | number; 
    label: string; 
    tooltip?: string;
    active?: boolean;
}) => {
    const content = (
        <div 
            className={`h-11 flex flex-col items-center justify-center gap-0 px-2.5 py-2 hover:bg-muted/50 transition-colors cursor-default ${
                active ? 'bg-primary/5' : ''
            }`}
        >
            <div className="flex items-center gap-1.5 mb-0.5">
                <Icon className="h-3.5 w-3.5 text-primary" />
                <span className="text-xs font-semibold truncate">{value}</span>
            </div>
            <span className="text-[9px] text-muted-foreground uppercase tracking-wide leading-tight">{label}</span>
        </div>
    );

    if (tooltip) {
        return (
            <TooltipProvider delayDuration={200}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        {content}
                    </TooltipTrigger>
                    <TooltipContent>
                        <p className="text-xs">{tooltip}</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    }

    return content;
};

export function ToolCard({ tool, onClick, variants }: ToolCardProps) {
    // Safely extract values
    const toolType = tool.type || "function";
    const functionName = tool.function?.name || "Unnamed Tool";
    const isAsync = tool.async ?? false;
    
    // Count parameters
    const parameters = tool.function?.parameters?.properties || {};
    const paramCount = Object.keys(parameters).length;
    const requiredCount = tool.function?.parameters?.required?.length || 0;
    
    // Count messages
    const messageCount = tool.messages?.length || 0;
    
    // Count knowledge bases for query tools
    const knowledgeBaseCount = tool.knowledgeBases?.length || 0;
    
    // Count files in knowledge bases
    const totalFileCount = tool.knowledgeBases?.reduce((sum, kb) => sum + (kb.fileIds?.length || 0), 0) || 0;
    
    // Count destinations for transfer/handoff tools
    const destinationCount = tool.destinations?.length || 0;
    
    // Get transfer mode for transferCall (from first destination if available)
    const firstDestination = tool.destinations?.[0] as any;
    const transferModeRaw = firstDestination?.transferPlan?.mode || 'standard';
    
    // Shorten transfer mode for display
    const transferModeMap: Record<string, string> = {
        'blind-transfer': 'Blind',
        'blind-transfer-add-summary-to-sip-header': 'Blind+SIP',
        'warm-transfer-say-message': 'Warm Msg',
        'warm-transfer-say-summary': 'Warm Sum',
        'warm-transfer-wait-for-operator-to-speak-first-and-then-say-message': 'Warm Wait+Msg',
        'warm-transfer-wait-for-operator-to-speak-first-and-then-say-summary': 'Warm Wait+Sum',
        'warm-transfer-twiml': 'Warm TwiML',
        'warm-transfer-experimental': 'Warm Exp',
        'standard': 'Standard'
    };
    const transferMode = transferModeMap[transferModeRaw] || 'Standard';
    
    // Get handoff destination type
    const handoffDestType = firstDestination?.type ? 
        firstDestination.type.charAt(0).toUpperCase() + firstDestination.type.slice(1) : 'Unknown';
    
    // Get context engineering plan for handoff
    const contextPlan = firstDestination?.contextEngineeringPlan?.type || 'all';
    const contextPlanDisplay = contextPlan === 'lastNMessages' 
        ? `Last ${firstDestination.contextEngineeringPlan.maxMessages}`
        : contextPlan.charAt(0).toUpperCase() + contextPlan.slice(1);
    
    // Knowledge base model (if configured)
    const kbModel = tool.knowledgeBases?.[0]?.model || null;
    
    // Check for auth
    const hasAuth = Object.keys(tool.server?.headers || {}).length > 0 || !!tool.server?.secret;
    
    // Check for server URL
    const hasServerUrl = !!tool.server?.url;
    
    // Count custom headers (function/mcp)
    const headerCount = Object.keys(tool.server?.headers || {}).length;
    
    // Check if secret is configured
    const hasSecret = !!tool.server?.secret;
    
    // Timeout
    const timeoutSeconds = tool.server?.timeoutSeconds || 20;
    
    // Format date
    const formattedDate = new Date(tool.createdAt).toLocaleDateString();
    
    const TypeIcon = TYPE_ICONS[toolType] || Webhook;

    return (
        <motion.div variants={variants}>
            <Card
                className="group relative overflow-hidden transition-all duration-200 hover:shadow-md cursor-pointer"
                onClick={onClick}
            >
                {/* Subtle gradient on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

                {/* Main Content */}
                <div className="relative p-5 space-y-4">
                    {/* Header with Icon, Title, and ID */}
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                            {/* Icon */}
                            <div className="relative flex-shrink-0">
                                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary/10 via-background to-primary/10 flex items-center justify-center ring-1 ring-primary/10 shadow-sm">
                                    <TypeIcon className="h-5 w-5 text-primary" />
                                </div>
                            </div>

                            {/* Title and Date */}
                            <div className="min-w-0 flex-1">
                                <h3 className="text-base font-semibold tracking-tight truncate">
                                    {functionName}
                                </h3>
                                {/* Date */}
                                <div className="flex items-center gap-1.5 mt-1">
                                    <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                                    <span className="text-xs text-muted-foreground">
                                        {formattedDate}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Tool ID on Right */}
                        <div onClick={(e) => e.stopPropagation()}>
                            <FadeId 
                                id={tool.id} 
                                label="Tool ID"
                                tooltipSide="top"
                            />
                        </div>
                    </div>

                    {/* Unified Configuration Grid with Dividers */}
                    <div className="rounded-lg border border-border overflow-hidden bg-muted/30">
                        <div className="grid grid-cols-3 divide-x divide-border">
                            {/* Row 1 - Type, Parameters, Server URL */}
                            {/* Type - Always First Cell */}
                            <StatButton
                                icon={TypeIcon}
                                value={TYPE_LABELS[toolType]}
                                label="Type"
                                tooltip={`Tool type: ${TYPE_LABELS[toolType]}`}
                            />
                            
                            {/* Parameters / Knowledge / Destinations */}
                            {toolType === "query" ? (
                                <StatButton
                                    icon={BookOpen}
                                    value={knowledgeBaseCount}
                                    label="Knowledge"
                                    tooltip={`${knowledgeBaseCount} knowledge base${knowledgeBaseCount === 1 ? '' : 's'} configured`}
                                    active={knowledgeBaseCount > 0}
                                />
                            ) : (toolType === "transferCall" || toolType === "handoff") ? (
                                <StatButton
                                    icon={toolType === "transferCall" ? PhoneForwarded : GitBranch}
                                    value={destinationCount}
                                    label={toolType === "transferCall" ? "Transfers" : "Handoffs"}
                                    tooltip={`${destinationCount} destination${destinationCount === 1 ? '' : 's'} configured`}
                                    active={destinationCount > 0}
                                />
                            ) : (toolType === "function" || toolType === "mcp") ? (
                                <StatButton
                                    icon={Blocks}
                                    value={paramCount}
                                    label="Parameters"
                                    tooltip={`${paramCount} parameter${paramCount === 1 ? '' : 's'} (${requiredCount} required)`}
                                    active={paramCount > 0}
                                />
                            ) : (
                                <StatButton
                                    icon={Blocks}
                                    value={0}
                                    label="Parameters"
                                    tooltip="No parameters configured"
                                />
                            )}

                            {/* Server URL */}
                            <StatButton
                                icon={Webhook}
                                value={hasServerUrl ? "Yes" : "No"}
                                label="Server URL"
                                tooltip={hasServerUrl ? "Server URL configured" : "No server URL"}
                                active={hasServerUrl}
                            />
                        </div>
                        
                        {/* Horizontal Divider */}
                        <div className="border-t border-border" />
                        
                        <div className="grid grid-cols-3 divide-x divide-border">
                            {/* Row 2 - Dynamic based on tool type */}
                            
                            {/* Cell 4: Messages (all types) */}
                            <StatButton
                                icon={MessageSquare}
                                value={messageCount}
                                label="Messages"
                                tooltip={`${messageCount} message${messageCount === 1 ? '' : 's'} configured`}
                                active={messageCount > 0}
                            />

                            {/* Cell 5: Dynamic based on tool type */}
                            {toolType === "function" || toolType === "mcp" ? (
                                <StatButton
                                    icon={KeyRound}
                                    value={headerCount}
                                    label="Headers"
                                    tooltip={`${headerCount} custom header${headerCount === 1 ? '' : 's'} configured`}
                                    active={headerCount > 0}
                                />
                            ) : toolType === "transferCall" ? (
                                <StatButton
                                    icon={ArrowRightLeft}
                                    value={transferMode}
                                    label="Mode"
                                    tooltip={`Transfer mode: ${transferModeRaw.split('-').join(' ')}`}
                                />
                            ) : toolType === "handoff" ? (
                                <StatButton
                                    icon={GitBranch}
                                    value={handoffDestType}
                                    label="Dest Type"
                                    tooltip={`Destination type: ${handoffDestType}`}
                                />
                            ) : toolType === "query" ? (
                                <StatButton
                                    icon={Zap}
                                    value={isAsync ? "Yes" : "No"}
                                    label="Async"
                                    tooltip={isAsync ? "Tool executes asynchronously" : "Tool executes synchronously"}
                                    active={isAsync}
                                />
                            ) : (
                                <StatButton
                                    icon={Zap}
                                    value={isAsync ? "Yes" : "No"}
                                    label="Async"
                                    tooltip={isAsync ? "Tool executes asynchronously" : "Tool executes synchronously"}
                                    active={isAsync}
                                />
                            )}

                            {/* Cell 6: Dynamic based on tool type */}
                            {toolType === "function" || toolType === "mcp" ? (
                                <StatButton
                                    icon={Clock}
                                    value={`${timeoutSeconds}s`}
                                    label="Timeout"
                                    tooltip={`${timeoutSeconds} second timeout`}
                                />
                            ) : toolType === "handoff" ? (
                                <StatButton
                                    icon={ListTree}
                                    value={contextPlanDisplay}
                                    label="Context"
                                    tooltip={`Context plan: ${contextPlanDisplay}`}
                                />
                            ) : toolType === "query" ? (
                                <StatButton
                                    icon={FileText}
                                    value={totalFileCount}
                                    label="Files"
                                    tooltip={`${totalFileCount} file${totalFileCount === 1 ? '' : 's'} in knowledge bases`}
                                    active={totalFileCount > 0}
                                />
                            ) : (
                                <StatButton
                                    icon={Zap}
                                    value={isAsync ? "Yes" : "No"}
                                    label="Async"
                                    tooltip={isAsync ? "Tool executes asynchronously" : "Tool executes synchronously"}
                                    active={isAsync}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </Card>
        </motion.div>
    );
}
